import React, { useEffect, useState } from "react";
import Title from "../../Text/Title/Title";
import useApiRequests from "../../../hooks/useApiRequests.tsx";
import { useNotification } from "../../../hooks/useNotification";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Grid, Button } from "@mui/material";
import { FiChevronRight } from "react-icons/fi";

function DashboardV2Filter() {
  const { category } = useParams();
  const { notify } = useNotification();
  const { getDashboardV2Tasks } = useApiRequests();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState<any[]>([]);

  const titles: Record<string, string> = {
    "prioritarias-aprovadas": "Tarefas prioritárias aprovadas",
    "nao-prioritarias-aprovadas": "Tarefas não prioritárias aprovadas",
    "prioritarias-nao-aprovadas": "Tarefas prioritárias não aprovadas",
    "prioritarias-nao-atribuidas": "Tarefas prioritárias não atribuídas aguardando",
    "prioritarias-aguardando-mais-1-dia": "Tarefas prioritárias aguardando há mais de 1 dia",
    "nao-prioritarias-aguardando-7-dias": "Tarefas não prioritárias aguardando há 7 dias",
    "nao-prioritarias-aguardando-14-dias": "Tarefas não prioritárias aguardando há 14 dias",
    "nao-prioritarias-aguardando-30-dias": "Tarefas não prioritárias aguardando há 30 dias ou mais",
    "prazos-alterados": "Prazos alterados (últimos 30 dias)",
  };

  useEffect(() => {
    const fetchTasks = async () => {
      if (!category) return;
      const res = await getDashboardV2Tasks(category);
      if (res.success) {
        setTasks(res.data || []);
      } else {
        notify(res.msg || "Erro ao carregar tarefas", { type: "error" });
      }
    };
    fetchTasks();
  }, [category]);

  const isPriorCategory = (cat?: string) =>
    (cat || "").toLowerCase().includes("prioritarias");

  const isPriorTask = (t: any) => {
    const p = (t?.priority ?? "").toString().toLowerCase();
    return p.includes("veryhigh") || p.includes("high") || p.includes("alta") || p === "2" || p === "3" || isPriorCategory(category);
  };

  const statusColor = (name?: string, prior?: boolean) => {
    if (prior) return { bg: "rgba(255,255,255,0.10)", border: "1px solid rgba(255,255,255,0.28)", color: "#e0e0e0" };
    return { bg: "rgba(25,118,210,0.12)", border: "1px solid rgba(25,118,210,0.25)", color: "#90caf9" };
  };

  const formatDate = (date?: string) => {
    if (!date) return "Não definido";
    return new Date(date).toLocaleDateString("pt-BR");
  };

  const isDeadlineChanges = category === "prazos-alterados";

  return (
    <div className="page-container page-container-wide">
      <Title>{titles[category || ""] || "Tarefas"}</Title>

      <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 2 }}>
        <Button variant="outlined" onClick={() => navigate("/dashboardv2")}>Voltar</Button>
        <Box sx={{ fontSize: "0.95rem", color: "#90caf9" }}>Total: {tasks.length}</Box>
      </Box>

      <Box>
        <Grid container spacing={2}>
          {tasks.map((t: any) => (
            <Grid item xs={12} key={t.trelloId || t.id}>
              <Box
                sx={{
                  borderRadius: "14px",
                  p: 2,
                  display: "grid",
                  gridTemplateColumns: isDeadlineChanges ? "1fr" : "1fr auto",
                  gap: 2,
                  alignItems: "center",
                  minHeight: 68,
                  bgcolor: isDeadlineChanges
                    ? "rgba(255,152,0,0.08)"
                    : isPriorTask(t)
                      ? "rgba(255,255,255,0.08)"
                      : "rgba(255,255,255,0.04)",
                  boxShadow: "0 6px 18px rgba(0,0,0,0.2)",
                  border: isDeadlineChanges
                    ? "1px solid rgba(255,152,0,0.25)"
                    : isPriorTask(t)
                      ? "1px solid rgba(255,255,255,0.22)"
                      : "1px solid rgba(255,255,255,0.08)",
                  backdropFilter: "blur(6px)",
                  transition: "transform 0.15s ease",
                  "&:hover": { transform: "translateY(-1px)" },
                }}
              >
                {isDeadlineChanges ? (
                  <>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Box
                        sx={{
                          color: "#ffb74d",
                          cursor: "pointer",
                          fontWeight: 600,
                          lineHeight: 1.4,
                        }}
                        onClick={() => navigate(`/task/view/${t.trelloId}`)}
                      >
                        {t.title}
                      </Box>
                      <FiChevronRight color="#ffb74d" />
                    </Box>
                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1.5, mt: 1 }}>
                      <Box sx={{ fontSize: "0.9rem", color: "#e0e0e0" }}>
                        <strong>Prazo anterior:</strong> {formatDate(t.oldFinishDate)}
                      </Box>
                      <Box sx={{ fontSize: "0.9rem", color: "#e0e0e0" }}>
                        <strong>Novo prazo:</strong> {formatDate(t.newFinishDate)}
                      </Box>
                      <Box sx={{ fontSize: "0.9rem", color: "#e0e0e0" }}>
                        <strong>Alterado por:</strong> {t.changedByName}
                      </Box>
                      <Box sx={{ fontSize: "0.9rem", color: "#e0e0e0" }}>
                        <strong>Data da alteração:</strong> {formatDate(t.changedAt)}
                      </Box>
                    </Box>
                    <Box sx={{ fontSize: "0.9rem", color: "#bdbdbd", mt: 1 }}>
                      <strong>Justificativa:</strong> {t.justification}
                    </Box>
                  </>
                ) : (
                  <>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Box
                        sx={{
                          color: isPriorTask(t) ? "#e0e0e0" : "#90caf9",
                          cursor: "pointer",
                          fontWeight: 600,
                          lineHeight: 1.4,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                        onClick={() => navigate(`/task/view/${t.trelloId}`)}
                      >
                        {t.title}
                      </Box>
                      <FiChevronRight color={isPriorTask(t) ? "#e0e0e0" : "#90caf9"} />
                    </Box>
                    <Box
                      sx={{
                        fontSize: "0.85rem",
                        fontWeight: 700,
                        px: 1.5,
                        py: 0.5,
                        borderRadius: "999px",
                        textTransform: "capitalize",
                        ...statusColor(t.listName, isPriorTask(t)),
                      }}
                    >
                      {t.listName}
                    </Box>
                  </>
                )}
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
    </div>
  );
}

export default DashboardV2Filter;
